import React, { createContext, useContext, useState, useEffect } from 'react';

const GlobalDataContext = createContext();

export const useGlobalData = () => {
  const context = useContext(GlobalDataContext);
  if (!context) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
};

export const GlobalDataProvider = ({ children }) => {
  const [globalData, setGlobalData] = useState({
    tokenPrices: {},
    rexPools: {},
    lastUpdate: {
      tokenPrices: null,
      rexPools: null
    },
    isLoading: {
      tokenPrices: true,
      rexPools: true
    },
    error: {
      tokenPrices: null,
      rexPools: null
    }
  });

  const apiUrl = process.env.REACT_APP_API_URL;

  // Intervalos de actualización (en milisegundos)
  const UPDATE_INTERVALS = {
    TOKEN_PRICES: 1 * 60 * 1000,    // 1 minuto
    REX_POOLS: 5 * 60 * 1000,       // 5 minutos
  };

  // Función para obtener todos los precios de tokens
  const fetchTokenPrices = async () => {
    try {
      
      const response = await fetch(`${apiUrl}/global/all_current_prices`);
      const data = await response.json();
      
      if (response.ok && data.status === 'OK') {
        setGlobalData(prev => ({
          ...prev,
          tokenPrices: data.prices || {},
          lastUpdate: {
            ...prev.lastUpdate,
            tokenPrices: data.lastUpdate || Date.now()
          },
          isLoading: {
            ...prev.isLoading,
            tokenPrices: false
          },
          error: {
            ...prev.error,
            tokenPrices: null
          }
        }));
        
      } else {
        throw new Error(data.error || 'Failed to fetch token prices');
      }
    } catch (error) {
      console.error('GlobalDataContext: Error fetching token prices:', error);
      setGlobalData(prev => ({
        ...prev,
        isLoading: {
          ...prev.isLoading,
          tokenPrices: false
        },
        error: {
          ...prev.error,
          tokenPrices: error.message
        }
      }));
    }
  };

  // Función para obtener información de REX pools
  const fetchRexPools = async () => {
    try {
      
      const response = await fetch(`${apiUrl}/global/rex_pools`);
      const data = await response.json();
      
      if (response.ok && data.status === 'OK') {
        setGlobalData(prev => ({
          ...prev,
          rexPools: data.pools || {},
          lastUpdate: {
            ...prev.lastUpdate,
            rexPools: data.lastUpdate || Date.now()
          },
          isLoading: {
            ...prev.isLoading,
            rexPools: false
          },
          error: {
            ...prev.error,
            rexPools: null
          }
        }));
        
      } else {
        throw new Error(data.error || 'Failed to fetch REX pools');
      }
    } catch (error) {
      console.error('GlobalDataContext: Error fetching REX pools:', error);
      setGlobalData(prev => ({
        ...prev,
        isLoading: {
          ...prev.isLoading,
          rexPools: false
        },
        error: {
          ...prev.error,
          rexPools: error.message
        }
      }));
    }
  };

  // Función para obtener REX info optimizada de una cuenta específica
  const fetchOptimizedRexInfo = async (blockchain, accountName) => {
    try {
      const response = await fetch(`${apiUrl}/${blockchain}/get_rex_info_optimized/${accountName}`);
      const data = await response.json();
      
      if (response.ok) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to fetch REX info');
      }
    } catch (error) {
      console.error('GlobalDataContext: Error fetching optimized REX info:', error);
      throw error;
    }
  };

  // Configurar actualizaciones automáticas
  useEffect(() => {
    // Cargar datos iniciales
    fetchTokenPrices();
    fetchRexPools();

    // Configurar intervalos de actualización
    const priceInterval = setInterval(fetchTokenPrices, UPDATE_INTERVALS.TOKEN_PRICES);
    const rexInterval = setInterval(fetchRexPools, UPDATE_INTERVALS.REX_POOLS);


    // Cleanup intervals on unmount
    return () => {
      clearInterval(priceInterval);
      clearInterval(rexInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Pausar actualizaciones cuando la pestaña no está visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTokenPrices();
        fetchRexPools();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funciones helper para obtener datos específicos
  const getTokenPrice = (tokenSymbol) => {
    return globalData.tokenPrices[tokenSymbol] || null;
  };

  const getRexPool = (chainId) => {
    return globalData.rexPools[chainId] || null;
  };

  const isTokenPriceLoading = () => {
    return globalData.isLoading.tokenPrices;
  };

  const isRexPoolLoading = () => {
    return globalData.isLoading.rexPools;
  };

  const getTokenPriceError = () => {
    return globalData.error.tokenPrices;
  };

  const getRexPoolError = () => {
    return globalData.error.rexPools;
  };

  // Función para forzar actualización manual
  const refreshData = async () => {
    await Promise.all([
      fetchTokenPrices(),
      fetchRexPools()
    ]);
  };

  const value = {
    // Datos
    tokenPrices: globalData.tokenPrices,
    rexPools: globalData.rexPools,
    lastUpdate: globalData.lastUpdate,
    
    // Estados de carga
    isTokenPriceLoading,
    isRexPoolLoading,
    
    // Errores
    getTokenPriceError,
    getRexPoolError,
    
    // Funciones helper
    getTokenPrice,
    getRexPool,
    fetchOptimizedRexInfo,
    refreshData,
    
    // Funciones de actualización manual
    fetchTokenPrices,
    fetchRexPools
  };

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
};

export default GlobalDataContext;
